import {Component, OnDestroy, OnInit, ChangeDetectorRef} from '@angular/core';
import {FormBuilder, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {environment} from 'src/environments/environment';
import {CustomValidators} from 'src/app/shared/validators/custom-validator';
import {UsernameValidator} from "../shared/validators/username-validator";

// models
import {CustomerLogin, PasswordPolicy} from '../shared/model/models';

// services
import {FacadeService} from '../shared/service-facade/facade.service';
import {ShoppingCartService} from "../shared/services/shopping-cart.service";
import {ApplicationLogDetailsRequest, ApplicationLogStepRequest, Log, Step} from "leia-api";
import {NGXLogger} from "ngx-logger";
import {UtilitiesService} from "../shared/services/utilities.service";
import {StatusTrackerInterface} from "../shared/interfaces/status-tracker.interface";
import {Sections, SectionStates, SectionStatus} from "../status-tracker/model/section-status";
import {SectionStatusService} from "../status-tracker/section-status.service";
import {OnboardingRequest} from '../shared/model/onboarding-request.model';

import NameEnum = Step.NameEnum;
import {Observable, Subject} from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {SettingsEnum} from "../shared/model/settings.enum";
import {BrandType} from "../signup/models/brand-type";
import {OnboardingService} from "../shared/services/onboarding.service";
import {MainLayoutService} from "../shared/services/main-layout.service";
import { SignupService } from '../signup/service/signup.service';
import OnboardingStateEnum = ApplicationLogStepRequest.OnboardingStateEnum;
import OnboardingSourceEnum = ApplicationLogDetailsRequest.OnboardingSourceEnum;
import OnboardingStepEnum = ApplicationLogStepRequest.OnboardingStepEnum;
import {CoreService} from "../shared/services/core.service";

const INVALID_CLASS = 'is-invalid';
const USERNAME_CONTROL_NAME = 'username';
const PASSWORD_CONTROL_NAME = 'password';
@Component({
  selector: 'app-login-details',
  templateUrl: './login-details.component.html',
  styleUrls: ['./login-details.component.scss']
})
export class LoginDetailsComponent implements OnInit, StatusTrackerInterface, OnDestroy {
  isInJointMaintenanceFlow: boolean = false;
  public loginInfoForm: FormGroup = this.formBuilder.group({});
  public passwordPolicy!: PasswordPolicy;
  minorSavings : boolean = false;
  enrollmentError: boolean = false;
  enrollmentErrorText: string = "";
  hidePassword = true;
  hideConfirmPassword = true;
  applicationHasSecondaryApplicant = false;

  destroySubject$: Subject<void> = new Subject();
  isMobile$: Observable<boolean>;

  shoppingCartOnboardingRequest: OnboardingRequest;

  // username min/max lengths
  usernameMin: number = 6;
  usernameMax: number = 50;

  showUsernameAvailableMessage: Observable<boolean>;

  get usernameClass() {
    return this._getClassForInvalidAndDirtyFormControl(USERNAME_CONTROL_NAME);
  }

  get passwordClass() {
    return this._getClassForInvalidAndDirtyFormControl(PASSWORD_CONTROL_NAME);
  }

  private _getClassForInvalidAndDirtyFormControl(controlName: string): string {
    const formControl = this.loginInfoForm?.controls[controlName];

    if (formControl && formControl.invalid && formControl.dirty) {
      return INVALID_CLASS;
    }

    return '';
  }

  /**
   * Constructor
   * @param formBuilder form builder instance.
   * @param wizard wizard this form belongs too.
   * @param usernameValidator Username validator.
   * @param facadeService Facade service.
   * @param shoppingCart Shopping cart service.
   * @param statusTrackerService Status tracker service.
   * @param logger ngx logger.
   * @param utils Utilities service.
   * @param onboardingService Onboarding service.
   * @param changeDetector Change Detector.
   * @param mainLayout Main layout service.
   */
  constructor(private formBuilder: FormBuilder,
              private usernameValidator: UsernameValidator,
              private facadeService: FacadeService,
              private shoppingCart: ShoppingCartService,
              private statusTrackerService: SectionStatusService,
              private logger: NGXLogger,
              private utils: UtilitiesService,
              private onboardingService: OnboardingService,
              private changeDetector: ChangeDetectorRef,
              private mainLayout: MainLayoutService,
              private signupService:SignupService,
              private coreService: CoreService
  ) {
    this.shoppingCartOnboardingRequest = new OnboardingRequest();
    this.isMobile$ = this.facadeService.isMobile$;
    this.facadeService.isInJointMaintenanceFlow$.pipe(takeUntil(this.destroySubject$)).subscribe(value => {
      this.isInJointMaintenanceFlow = value;
    });

    this.showUsernameAvailableMessage = this.usernameValidator.useValidator;
  }

  /**
   * On Init.
   */
  ngOnInit(): void {
    this.coreService.disableBackButton();

    this.mainLayout.setPageTitle(`Create Login`);

    this.statusTrackerService.updateStatusTrackerSectionStatus(Sections.VerifyIdentification, SectionStates.complete);

    // set min/max username length from settings
    this.usernameMin = Number(this.facadeService.getStorageItem(SettingsEnum.BrandUsernameMinLength)) ?? 8
    this.usernameMax = Number(this.facadeService.getStorageItem(SettingsEnum.BrandUsernameMaxLength)) ?? 48

    this.minorSavings = this.facadeService.getIsMinor();

    if(!this.minorSavings) {
      this.loginInfoForm = this.createLoginInfoForm();
      this.setupData();

      const brandId = this.facadeService.getBrandId();
      const brandName = Number(brandId) === BrandType.BaskBank ? "BaskBank" : "";

      this.facadeService.getPasswordPolicy(brandName).then(response => {
        this.passwordPolicy = response;
        this.logger.debug("LoginDetailsComponent-getSettings response:" + JSON.stringify(this.passwordPolicy));

        this.setupPasswordValidators();
        this.setupStatusTracker();
        this.setupValidationTrigger();

        if (environment.development) {
          this.logger.debug("login-details - In Development mode.")
          //pre fill removed
        }

        this.prefillAvailableReviewData();

        this.signupService.addFormToTrackState(this.loginInfoForm);
      });
    }

    this.usernameValidator.useValidator.pipe(takeUntil(this.destroySubject$)).subscribe(useValidator => {
      if (useValidator) { return; }

      const usernameControl = this.loginInfoForm.controls['username'];

      usernameControl.clearAsyncValidators();
      usernameControl.updateValueAndValidity();

      this.changeDetector.detectChanges();
    });

    this.facadeService.deactivatePasswordPolicy.pipe(takeUntil(this.destroySubject$)).subscribe(deactivate => {
      this.setupPasswordValidators();
      this.setupStatusTracker();
      this.setupValidationTrigger();
      this.prefillAvailableReviewData();

      this.changeDetector.detectChanges();
    });
  }

  ngOnDestroy(): void {
    if(!this.minorSavings) {
      this.statusTrackerService.updateStatusTrackerSectionStatus(Sections.CreateLogin, SectionStates.notStarted, false);
      this.destroySubject$.next();
    }
  }

  private prefillAvailableReviewData(): void {
    const { userName } = this.shoppingCartOnboardingRequest.customerLogin;
    if (!!userName) {
      this.loginInfoForm.controls[USERNAME_CONTROL_NAME].setValue(userName);
    }
  }

  private formIsDirty(): boolean {
    return this.loginInfoForm!.controls["username"]?.dirty ||
      this.loginInfoForm!.controls["password"]?.dirty ||
      this.loginInfoForm!.controls["confirmPassword"]?.dirty;
  }

  /**
   * Setup the initial data for the component
   */
  setupData(): void {
    this.shoppingCartOnboardingRequest = this.facadeService.getShoppingCartOnboardingRequest();
    this.applicationHasSecondaryApplicant = this.shoppingCartOnboardingRequest.applicants.length > 1;
  }

  /**
   * Create form with validators.
   */
  createLoginInfoForm(): FormGroup {
    let validators: ValidatorFn[] = [Validators.required];

    return this.formBuilder.group(
      {
        username: [
          null,
          {
            validators: [Validators.required, Validators.minLength(this.usernameMin), Validators.maxLength(this.usernameMax), Validators.pattern("[a-zA-Z0-9]+$")],
            asyncValidators: [this.usernameValidator],
            updateOn: 'blur'
          }
        ],
        password: [
          null,
          validators
        ],
        confirmPassword: [null, Validators.compose([Validators.required])]
      },
      {
        // check whether our password and confirm password match
        validator: CustomValidators.passwordMatchValidator
      }
    );
  }

  private setupPasswordValidators() {
    let validators: ValidatorFn[] = [Validators.required];

    let checkForNumberRegEx = new RegExp(this.facadeService.getStorageItem("checkForNumber") != undefined ?
      this.facadeService.getStorageItem("checkForNumber") : "\\d");
    let checkForUppercaseRegEx = new RegExp(this.facadeService.getStorageItem("checkForUppercase") != undefined ?
      this.facadeService.getStorageItem("checkForUppercase") : "[A-Z]");
    let checkForLowercaseRegEx = new RegExp(this.facadeService.getStorageItem("checkForLowercase") != undefined ?
      this.facadeService.getStorageItem("checkForLowercase") : "[a-z]");
    let checkForSpecialCharacterRegEx = new RegExp(this.facadeService.getStorageItem("checkForSpecialCharacter") != undefined ?
      this.facadeService.getStorageItem("checkForSpecialCharacter") : "[ !@#$%^&*()_+\\-=\\[\\]{};':\"\\\\|,.<>\\/?]");

    // Add validators dynamically
    // Check for numbers
    if (this.passwordPolicy?.numbersRequired) {
      validators.push(CustomValidators.patternValidator(checkForNumberRegEx, {
        hasNumber: true
      }));
    }

    // Min length.
    validators.push(Validators.minLength(this.passwordPolicy?.minPasswordLength));
    // Max Length.
    validators.push(Validators.maxLength(this.passwordPolicy?.maxPasswordLength));

    // Uppercase character
    if (this.passwordPolicy?.upperCaseRequired) {
      // check whether the entered password has upper case letter
      validators.push(
        CustomValidators.patternValidator(checkForUppercaseRegEx, {
          hasCapitalCase: true
        }));
    }

    // Lowercase character
    if (this.passwordPolicy?.lowerCaseRequired) {
      // check whether the entered password has a lower case letter
      validators.push(
        CustomValidators.patternValidator(checkForLowercaseRegEx, {
          hasSmallCase: true
        }));
    }

    // Special character
    if (this.passwordPolicy?.specialCharRequired) {
      validators.push(
        CustomValidators.patternValidator(checkForSpecialCharacterRegEx, {
            hasSpecialCharacters: true
          }
        ));
    }

    // Add all the validators from above to the password
    this.loginInfoForm.controls["password"].setValidators(validators);
  }

  private createCustomerLogin(): CustomerLogin {
    const customerLogin = new CustomerLogin();
    customerLogin.userName = this.loginInfoForm!.controls["username"].value;
    customerLogin.password = this.loginInfoForm!.controls["password"].value;
    this.onboardingService.setUsername(customerLogin.userName);

    return customerLogin;
  }

  /**
   * Update the application log on leia service.
   */
  updateApplication(state: Step.StateEnum, onboardingStateEnum: OnboardingStateEnum, customerLogin: CustomerLogin, log: Log, applicationLogDetailsRequest: ApplicationLogDetailsRequest): Promise<void> {
    let applicationRequest = this.facadeService.getShoppingCartApplicationLogsRequest();
    let step = this.utils.createStep(NameEnum.CaliperUserEnrollment, state, [log]);
    let applicationLogStepRequest = this.utils.createApplicationLogStepRequest(OnboardingStepEnum.CaliperUserEnrollment, onboardingStateEnum, OnboardingSourceEnum.Bb8, [applicationLogDetailsRequest]);

    const isPasswordValid = this.loginInfoForm!.controls["password"].valid && this.loginInfoForm!.controls["confirmPassword"].valid;
    const { userName, password } = customerLogin;

    applicationRequest.step = step;
    applicationRequest.applicationLogStepRequest = applicationLogStepRequest;

    applicationRequest.userName = userName;
    applicationRequest.isPasswordValid = isPasswordValid;

    // fix for username mapping issue on applicant
    applicationRequest.applicants?.forEach(applicant => {
      if (applicant.primary) {
        applicant.userName = userName;
        applicant.isPasswordValid = isPasswordValid;
      }
    });

    this.shoppingCartOnboardingRequest.customerLogin.userName = userName;
    this.shoppingCartOnboardingRequest.customerLogin.password = password;

    this.facadeService.setShoppingCartOnboardingRequest(this.shoppingCartOnboardingRequest);
    this.facadeService.setShoppingCartApplicationLogsRequest(applicationRequest);
    return this.facadeService.updateApplication();
  }

  /**
   * Check if password field has error.
   * @param error Error type
   */
  passwordHasError(error: string): boolean {
    return this.requiredFieldHasError('password', error);
  }
  /**
   * Check if confirm password field has error.
   * @param error Error type
   */
  confirmPasswordHasError(error: string): boolean {
    return this.requiredFieldHasError('confirmPassword', error);
  }

  /**
   * Checks if a required field has a specific error
   * @param field
   * @param error
   * @returns boolean
   */
  requiredFieldHasError(field: string, error: string): boolean {
    const formControl = this.loginInfoForm!.controls[field];
    const hasRequiredError = formControl?.hasError('required');
    const hasError = formControl?.hasError(error);

    return !!formControl &&
      (hasRequiredError || hasError);
  }



  setupStatusTracker(): void {
    this.loginInfoForm!.statusChanges.pipe(takeUntil(this.destroySubject$)).subscribe(async status => {
      const isDirty = this.formIsDirty();
      const log = this.utils.createLog("BB8", `Login details, Status Change: ${status} touched: ${isDirty}`);
      const applicationLogDetailsRequest = this.utils.createApplicationLogDetailsRequest(OnboardingSourceEnum.Bb8, `Login details, Status Change: ${status} touched: ${isDirty}`);

      const customerLogin = this.createCustomerLogin();

      if (status === 'VALID') {
        this.statusTrackerService.updateStatusTrackerSectionStatus(Sections.CreateLogin, SectionStates.complete);
        this.signupService.allTrackerSectionsComplete.next(false);

        await this.updateApplication(Step.StateEnum.Success, OnboardingStateEnum.Success, customerLogin, log, applicationLogDetailsRequest);
      } else if (status === "INVALID" && isDirty ) {
        this.statusTrackerService.updateStatusTrackerSectionStatus(Sections.CreateLogin, SectionStates.notComplete);
      this.signupService.allTrackerSectionsComplete.next(true);

      } else {
        this.statusTrackerService.updateStatusTrackerSectionStatus(Sections.CreateLogin, SectionStates.notStarted);
      this.signupService.allTrackerSectionsComplete.next(true);

      }
    });
  }

  setupValidationTrigger(): void {
    this.statusTrackerService.onTriggerValidations.pipe(takeUntil(this.destroySubject$)).subscribe(() => {
      this.loginInfoForm!.markAllAsTouched();
      this.loginInfoForm!.updateValueAndValidity();
    });
  };
}
